import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import { CatalogReplacementSearch } from '@/components/catalog-replacement-search';
import { Checkbox } from '@/components/checkbox';
import { createStyles } from '@/contexts/theme-context';
import { InboundRenderActions } from '@/screens/order-inbounds/inbound-render-actions';
import {
  itemListItemKeyExtractor,
  OrderableItemList,
} from '@/components/orders/orderable-item-list';
import {
  ItemListOption,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import { ItemsContext_VariantsPackFieldsFragment } from '@/types';
import { ListSection } from '@/components/recycler-list';
import { OrderableItemListItem } from '@/components/orders/orderable-item-list-item';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAdaptAndGroupVariantPacks } from '@/hooks/use-adapt-and-group-item-list-options';
import { useEscapeKeyClose } from '@platform/shortcuts/hooks/use-escape-key-close';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useOrderInboundsStore } from '@/stores/order-inbounds-store';
import { VariantPackOrderCountsMap } from './types';
import { View } from 'react-native';

interface Props {
  alternativeMatches?: ItemsContext_VariantsPackFieldsFragment[];
  initialRememberSelection?: boolean;
  onClose?: () => void;
  onSetRememberSelection?: (rememberSelection: boolean) => void;
  onSwapItem?: (
    metadata: ItemListOptionMetadata,
    rememberSelection: boolean,
  ) => void | Promise<void>;
  previousMatches?: ItemsContext_VariantsPackFieldsFragment[];
  savedVariantPackOrderCountsMap?: VariantPackOrderCountsMap;
  substitutionsMatches?: ItemsContext_VariantsPackFieldsFragment[];
  variantPackUUID?: string;
}

export const SwapInboundItemSearchContent: React.FC<Props> = ({
  alternativeMatches,
  initialRememberSelection = false,
  onClose,
  onSetRememberSelection,
  onSwapItem,
  previousMatches,
  savedVariantPackOrderCountsMap,
  substitutionsMatches,
  variantPackUUID,
}) => {
  const [styles, theme] = useStyles();
  const adaptAndGroupVariantPacks = useAdaptAndGroupVariantPacks();

  const [rememberSelection, setRememberSelection] = useState(
    initialRememberSelection,
  );

  const isSwapInProgress = useOrderInboundsStore(
    (state) => state.isSwapInProgress,
  );

  useEscapeKeyClose(onClose);

  const { selectItemListOption } = useItemDetailContext();

  const initialSuggestions = useMemo<ListSection<ItemListOption>[]>(() => {
    const sections: ListSection<ItemListOption>[] = [];
    if (substitutionsMatches && substitutionsMatches.length > 0) {
      sections.push({
        data: adaptAndGroupVariantPacks(
          substitutionsMatches,
          undefined,
          savedVariantPackOrderCountsMap,
        ),
        key: i18n.t('swap_suggestions.substitutes'),
      });
    }
    if (previousMatches && previousMatches.length > 0) {
      sections.push({
        data: adaptAndGroupVariantPacks(
          previousMatches,
          undefined,
          savedVariantPackOrderCountsMap,
        ),
        key: i18n.t('swap_suggestions.past_matches'),
      });
    }
    if (alternativeMatches && alternativeMatches.length > 0) {
      sections.push({
        data: adaptAndGroupVariantPacks(
          alternativeMatches,
          undefined,
          savedVariantPackOrderCountsMap,
        ),
        key: i18n.t('swap_suggestions.other_alternatives'),
      });
    }
    return sections;
  }, [
    alternativeMatches,
    previousMatches,
    adaptAndGroupVariantPacks,
    savedVariantPackOrderCountsMap,
    substitutionsMatches,
  ]);

  const renderActions = useCallback(
    (sku: ItemListOptionMetadata) => {
      return (
        <InboundRenderActions
          isExistingVariantPack={Boolean(
            variantPackUUID && variantPackUUID === sku.variantPackUUID,
          )}
          onSelect={() => onSwapItem?.(sku, rememberSelection)}
        />
      );
    },
    [onSwapItem, variantPackUUID, rememberSelection],
  );

  return (
    <CatalogReplacementSearch
      emptySearchContent={
        initialSuggestions.length ? (
          <View style={styles.listContainer}>
            <OrderableItemList
              gridColumnCount={1}
              keyExtractor={(item: ItemListOption, index: number) => {
                // Items cannot be added or removed from the list.
                // Use index to create unique keys for identical items in different sections
                const baseKey = itemListItemKeyExtractor(item);
                return `${baseKey}-${index}`;
              }}
              listItemProps={{
                onSelect: (option) => {
                  selectItemListOption(
                    option,
                    {
                      source: 'inbound_swap_suggestion',
                    },
                    {
                      renderActions: (sku) => renderActions(sku),
                    },
                  );
                },
                renderMainContent: (option) => {
                  return (
                    <OrderableItemListItem
                      isFocused={false}
                      isInboundOrder
                      option={option}
                      renderActions={(sku) => renderActions(sku)}
                      showVariantPackOrderInfo
                      source="inbound_swap_suggestion"
                    />
                  );
                },
              }}
              renderSectionHeader={(item) => {
                return (
                  <View style={styles.sectionHeader}>
                    <Typography
                      color={theme.sk.color.text.secondary}
                      variant="heading-md"
                    >
                      {item.title}
                    </Typography>
                  </View>
                );
              }}
              sections={initialSuggestions}
              skipInteractionCheck
              stickyHeaderIndices={[]}
            />
          </View>
        ) : undefined
      }
      filterTrackingKey="inbound_swap_search.item_group_selector"
      isInboundOrder
      isLoading={isSwapInProgress}
      isModal
      onClose={onClose}
      renderActions={(sku) => (
        <InboundRenderActions
          isExistingVariantPack={Boolean(
            variantPackUUID && variantPackUUID === sku.variantPackUUID,
          )}
          onSelect={() => onSwapItem?.(sku, rememberSelection)}
        />
      )}
      searchParams={{
        includeOrderCounts: true,
        searchSource: 'inbound',
      }}
      showVariantPackOrderInfo
      skipListInteractionCheck
    >
      {onSetRememberSelection ? (
        <Checkbox
          checkProps={{
            stroke: theme.colors.white,
          }}
          getCheckboxStyle={(isSelected) => ({
            backgroundColor: isSelected
              ? theme.colors.primary
              : theme.colors.white,
            borderColor: isSelected
              ? theme.colors.primary
              : theme.colors.inactiveFill,
          })}
          isSelected={Boolean(rememberSelection)}
          label={i18n.t('remember_selection')}
          onSelect={() => {
            setRememberSelection((prev) => {
              const newRememberSelection = !prev;
              onSetRememberSelection(newRememberSelection);
              return newRememberSelection;
            });
          }}
          style={styles.checkbox}
          trackingData={{
            name: 'catalog_replacement_search.remember_selection.toggle',
            properties: {
              incomingRememberSelectionState: !rememberSelection,
            },
          }}
        />
      ) : null}
    </CatalogReplacementSearch>
  );
};

const useStyles = createStyles((theme) => ({
  checkbox: {
    paddingBottom: 16,
    paddingTop: 4,
  },
  listContainer: {
    flex: 1,
    marginTop: 32,
  },
  sectionHeader: {
    backgroundColor: theme.colors.white,
    paddingBottom: 12,
    paddingHorizontal: 16,
  },
}));
